import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Link,
  Menu,
  Portal,
  Spinner,
  Switch,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Fragment, useRef, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiMoreVertical } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root';
import { toaster } from '#components/ui/toaster';
import type {
  GetAgentsQuery,
  GetProcessorInstancesByAgentQuery,
} from '#graphql/generated';
import { DELETE_AGENT } from '#graphql/mutations/delete-agent';
import { RUN_AGENT } from '#graphql/mutations/run-agent';
import { UPSERT_AGENT } from '#graphql/mutations/upsert-agent';
import { GET_AGENTS } from '#graphql/queries/agents';
import { GET_PROCESSOR_INSTANCES_BY_AGENT } from '#graphql/queries/processor-instances-by-agent';

import { AgentModal } from './agents-page/agent-modal';

type AgentRow = GetAgentsQuery['agents']['agents'][number];
type AgentInstanceRow =
  GetProcessorInstancesByAgentQuery['processorInstancesByAgent'][number];

const TABLE_COL_SPAN = 8;

export function AgentsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<AgentRow | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const expandedIdsRef = useRef(expandedIds);
  expandedIdsRef.current = expandedIds;
  const [agentInstances, setAgentInstances] = useState<
    Record<string, AgentInstanceRow[]>
  >({});
  const [loadingInstancesForId, setLoadingInstancesForId] = useState<
    string | null
  >(null);

  const { data, loading, error, refetch } = useQuery(GET_AGENTS);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteAgent] = useMutation(DELETE_AGENT, {
    refetchQueries: [{ query: GET_AGENTS }],
  });
  const [upsertAgent] = useMutation(UPSERT_AGENT, {
    refetchQueries: [{ query: GET_AGENTS }],
  });
  const [runAgent] = useMutation(RUN_AGENT);

  const [loadInstances] = useLazyQuery(GET_PROCESSOR_INSTANCES_BY_AGENT, {
    fetchPolicy: 'network-only',
  });

  const rows = data?.agents?.agents ?? [];
  const filtered = rows.filter(
    (a) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.processorId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.cron.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const fetchInstancesForAgent = async (agentId: string) => {
    setLoadingInstancesForId(agentId);
    try {
      const { data: instData } = await loadInstances({
        variables: { agentId, limit: 10 },
      });
      setAgentInstances((prev) => ({
        ...prev,
        [agentId]: instData?.processorInstancesByAgent ?? [],
      }));
    } finally {
      setLoadingInstancesForId(null);
    }
  };

  const openCreate = () => {
    setModalMode('create');
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: AgentRow) => {
    setModalMode('edit');
    setEditing(row);
    setModalOpen(true);
  };

  const handleDelete = async (row: AgentRow) => {
    if (!window.confirm(`Delete agent "${row.name}"?`)) return;

    setDeletingId(row.id);
    try {
      const { data: delData } = await deleteAgent({
        variables: { id: row.id },
      });
      if (!delData?.deleteAgent?.deleted) {
        toaster.create({
          title: 'Not deleted',
          description: delData?.deleteAgent?.message ?? 'Unknown error',
          type: 'warning',
        });
        return;
      }

      toaster.create({
        title: 'Agent removed',
        type: 'success',
        duration: 2500,
      });
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setAgentInstances((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      await refetch();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Delete failed';
      toaster.create({ title: 'Error', description: message, type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRun = async (row: AgentRow) => {
    setRunningId(row.id);
    try {
      const { data: runData } = await runAgent({ variables: { id: row.id } });
      const res = runData?.runAgent;
      if (!res?.queued) {
        toaster.create({
          title: 'Could not queue run',
          description: res?.message ?? 'Unknown error',
          type: 'error',
        });
        return;
      }

      toaster.create({
        title: 'Run queued',
        description: 'Processor will run on the task worker shortly.',
        type: 'success',
        duration: 3000,
      });
      await refetch();
      if (expandedIdsRef.current.has(row.id)) {
        await fetchInstancesForAgent(row.id);
      }
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Run failed';
      toaster.create({ title: 'Error', description: message, type: 'error' });
    } finally {
      setRunningId(null);
    }
  };

  const handleToggleEnabled = async (row: AgentRow, next: boolean) => {
    setTogglingId(row.id);
    try {
      await upsertAgent({
        variables: {
          input: {
            id: row.id,
            name: row.name,
            cron: row.cron,
            processorId: row.processorId,
            parameters:
              row.parameters && typeof row.parameters === 'object'
                ? (row.parameters as object)
                : {},
            enabled: next,
          },
        },
      });
      await refetch();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Update failed';
      toaster.create({ title: 'Error', description: message, type: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleExpand = async (row: AgentRow) => {
    if (expandedIds.has(row.id)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      return;
    }

    setExpandedIds((prev) => new Set(prev).add(row.id));
    await fetchInstancesForAgent(row.id);
  };

  if (loading && !data) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          Agents
        </Heading>
        <Text>Loading…</Text>
      </PageContentRoot>
    );
  }

  if (error) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          Agents
        </Heading>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </PageContentRoot>
    );
  }

  return (
    <PageContentRoot>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          Agents
        </Heading>
        <Text color="fg.muted" fontSize="sm">
          Define cron schedules, processor ids, and parameters. On each tick,
          the server queues{' '}
          <Text as="span" fontFamily="mono">
            executeProcessor
          </Text>{' '}
          for matching agents (same path as Run). Use Run to trigger a deferred
          execution immediately.
        </Text>
        <HStack flexWrap="wrap" gap={2}>
          <Input
            placeholder="Search by name, processor, or cron…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            maxW="280px"
          />
          <Button size="sm" colorPalette="blue" onClick={openCreate}>
            Add agent
          </Button>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </HStack>

        {filtered.length === 0 ? (
          <Text color="fg.muted">
            {searchTerm ? 'No agents match your search.' : 'No agents yet.'}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader width="40px" aria-label="Expand" />
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Cron</Table.ColumnHeader>
                  <Table.ColumnHeader>Processor</Table.ColumnHeader>
                  <Table.ColumnHeader>Enabled</Table.ColumnHeader>
                  <Table.ColumnHeader>Last run</Table.ColumnHeader>
                  <Table.ColumnHeader>Updated</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((row) => (
                  <Fragment key={row.id}>
                    <Table.Row>
                      <Table.Cell verticalAlign="middle">
                        <IconButton
                          aria-label={
                            expandedIds.has(row.id)
                              ? 'Hide recent runs'
                              : 'Show recent runs'
                          }
                          variant="ghost"
                          size="xs"
                          onClick={() => void handleToggleExpand(row)}
                        >
                          {expandedIds.has(row.id) ? (
                            <FiChevronDown size={16} />
                          ) : (
                            <FiChevronRight size={16} />
                          )}
                        </IconButton>
                      </Table.Cell>
                      <Table.Cell fontWeight="medium">{row.name}</Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.cron}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.processorId}
                      </Table.Cell>
                      <Table.Cell>
                        <Switch.Root
                          checked={row.enabled}
                          disabled={togglingId === row.id}
                          onCheckedChange={(d) => {
                            void handleToggleEnabled(row, !!d.checked);
                          }}
                        >
                          <Switch.HiddenInput />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap" fontSize="sm">
                        {row.lastRunAt
                          ? new Date(row.lastRunAt).toLocaleString()
                          : '—'}
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap" fontSize="sm">
                        {new Date(row.updatedAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Menu.Root positioning={{ placement: 'bottom-end' }}>
                          <Menu.Trigger asChild>
                            <IconButton
                              aria-label={`Actions for ${row.name}`}
                              variant="ghost"
                              size="xs"
                              disabled={
                                runningId === row.id || deletingId === row.id
                              }
                            >
                              <FiMoreVertical />
                            </IconButton>
                          </Menu.Trigger>
                          <Portal>
                            <Menu.Positioner>
                              <Menu.Content>
                                <Menu.Item
                                  value="run"
                                  disabled={runningId === row.id}
                                  onClick={() => void handleRun(row)}
                                >
                                  Run
                                </Menu.Item>
                                <Menu.Item
                                  value="edit"
                                  onClick={() => openEdit(row)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  value="delete"
                                  color="fg.error"
                                  disabled={deletingId === row.id}
                                  onClick={() => void handleDelete(row)}
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Content>
                            </Menu.Positioner>
                          </Portal>
                        </Menu.Root>
                      </Table.Cell>
                    </Table.Row>
                    {expandedIds.has(row.id) ? (
                      <Table.Row key={`${row.id}-runs`}>
                        <Table.Cell colSpan={TABLE_COL_SPAN} py={3}>
                          {loadingInstancesForId === row.id ? (
                            <HStack gap={2}>
                              <Spinner size="sm" />
                              <Text fontSize="sm" color="fg.muted">
                                Loading runs…
                              </Text>
                            </HStack>
                          ) : (agentInstances[row.id] ?? []).length === 0 ? (
                            <Text fontSize="sm" color="fg.muted">
                              No recorded runs for this agent yet (runs queued
                              after this update include metadata to appear
                              here).
                            </Text>
                          ) : (
                            <Box overflowX="auto">
                              <Text fontSize="xs" color="fg.muted" mb={2}>
                                Last {agentInstances[row.id]?.length ?? 0}{' '}
                                processor instance
                                {(agentInstances[row.id]?.length ?? 0) === 1
                                  ? ''
                                  : 's'}{' '}
                                for this agent
                              </Text>
                              <Table.Root size="sm" variant="line">
                                <Table.Header>
                                  <Table.Row>
                                    <Table.ColumnHeader>
                                      Created
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader>
                                      Status
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader>
                                      Processor
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader>
                                      Error
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign="end">
                                      {' '}
                                    </Table.ColumnHeader>
                                  </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                  {(agentInstances[row.id] ?? []).map(
                                    (inst) => (
                                      <Table.Row key={inst.id}>
                                        <Table.Cell
                                          whiteSpace="nowrap"
                                          fontSize="xs"
                                        >
                                          {new Date(
                                            inst.createdAt,
                                          ).toLocaleString()}
                                        </Table.Cell>
                                        <Table.Cell fontSize="xs">
                                          {inst.status}
                                        </Table.Cell>
                                        <Table.Cell
                                          fontFamily="mono"
                                          fontSize="xs"
                                        >
                                          {inst.processorId}
                                        </Table.Cell>
                                        <Table.Cell
                                          fontSize="xs"
                                          maxW="220px"
                                          css={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                          title={inst.error ?? undefined}
                                        >
                                          {inst.error ?? '—'}
                                        </Table.Cell>
                                        <Table.Cell textAlign="end">
                                          <Link asChild color="blue.600">
                                            <RouterLink
                                              to={`/instance/${inst.id}`}
                                            >
                                              View
                                            </RouterLink>
                                          </Link>
                                        </Table.Cell>
                                      </Table.Row>
                                    ),
                                  )}
                                </Table.Body>
                              </Table.Root>
                            </Box>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ) : null}
                  </Fragment>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
      <AgentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        editing={editing}
        onSaved={() => void refetch()}
      />
    </PageContentRoot>
  );
}
